"use client";

import { useCallback, useState } from "react";
import Link from "next/link";
import { motion, type PanInfo } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FaApple,
  FaChevronLeft,
  FaChevronRight,
  FaGlobe,
} from "react-icons/fa6";

import QrAndroid from "@/public/card/qr-android.svg";
import QrIos from "@/public/card/qr-ios.svg";
import QrWeb from "@/public/card/qr-web.svg";

import GooglePlayIcon from "@/app/components/Utils/GooglePlayIcon";
import { useAppTarget, type AppDestination } from "@/app/hooks/useAppUrl";
import {
  trackCtaClick,
  trackEvent,
} from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";

type Slide = {
  id: AppDestination;
  /** Pre-generated QR — see scripts/generate-card-qr.mjs. */
  Qr: React.ElementType;
  icon: React.ReactNode;
  labelKey: string;
  storeKey: string;
  deviceKey: string;
};

// Order matches how the card is pitched at an event: the web app works for
// everyone, then the two native stores.
const SLIDES: Slide[] = [
  {
    id: "web",
    Qr: QrWeb,
    icon: <FaGlobe className="text-base" aria-hidden="true" />,
    labelKey: "card.platform.web.label",
    storeKey: "card.platform.web.store",
    deviceKey: "card.platform.web.devices",
  },
  {
    id: "android",
    Qr: QrAndroid,
    icon: <GooglePlayIcon className="w-4 h-4" />,
    labelKey: "card.platform.android.label",
    storeKey: "card.platform.android.store",
    deviceKey: "card.platform.android.devices",
  },
  {
    id: "ios",
    Qr: QrIos,
    icon: <FaApple className="text-base" aria-hidden="true" />,
    labelKey: "card.platform.ios.label",
    storeKey: "card.platform.ios.store",
    deviceKey: "card.platform.ios.devices",
  },
];

/** Past this drag distance (or flick speed) the swipe counts as "next slide". */
const SWIPE_DISTANCE = 60;
const SWIPE_VELOCITY = 350;

/**
 * The three install QR codes, one per platform, on a swipeable track.
 *
 * Shown on /card at events: the visitor picks their platform and scans, so the
 * QR is deliberately the largest thing on screen and every slide stays one
 * gesture away.
 */
export default function QrCarousel() {
  const intl = useIntl();
  const { destination } = useAppTarget();
  // Links are decorated with the visit's attribution after hydration; the QR images stay the
  // plain store/app URLs (scripts/generate-card-qr.mjs).
  const links: Record<AppDestination, string> = {
    web: useAppLink("card", { target: "web" }).href,
    android: useAppLink("card", { target: "android" }).href,
    ios: useAppLink("card", { target: "ios" }).href,
  };
  // The visitor's platform is only known once we're in the browser, so it can
  // only ever be an opening guess: as soon as the visitor picks a slide
  // themselves, their choice wins for good.
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const guessedIndex = Math.max(
    0,
    SLIDES.findIndex((slide) => slide.id === destination),
  );
  const index = pickedIndex ?? guessedIndex;

  const goTo = useCallback(
    (next: number, how: string) => {
      const clamped = Math.max(0, Math.min(SLIDES.length - 1, next));
      if (clamped !== index) {
        trackEvent("card-qr-slide", { platform: SLIDES[clamped].id, how });
      }
      setPickedIndex(clamped);
    },
    [index],
  );

  const onDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const { offset, velocity } = info;
      const forward =
        offset.x < -SWIPE_DISTANCE || velocity.x < -SWIPE_VELOCITY;
      const backward = offset.x > SWIPE_DISTANCE || velocity.x > SWIPE_VELOCITY;
      if (forward) goTo(index + 1, "swipe");
      else if (backward) goTo(index - 1, "swipe");
    },
    [goTo, index],
  );

  return (
    <section
      className="flex w-full min-w-0 flex-col items-center"
      role="group"
      aria-roledescription="carousel"
      aria-label={intl.formatMessage({ id: "card.qr.title" })}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") goTo(index + 1, "keyboard");
        if (event.key === "ArrowLeft") goTo(index - 1, "keyboard");
      }}
    >
      {/* Platform picker — doubles as the carousel's dots. card.qr.title is
          still the section's accessible name, it just isn't drawn. */}
      <div className="flex items-center gap-1 rounded-full bg-blue-ulight p-1">
        {SLIDES.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(slideIndex, "tab")}
            aria-pressed={slideIndex === index}
            className={`flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-all duration-200 lg:px-3.5 lg:text-sm ${
              slideIndex === index
                ? "bg-surface text-title-blue shadow-sm"
                : "text-gray-main hover:text-title-blue"
            }`}
          >
            {slide.icon}
            <FormattedMessage id={slide.labelKey} />
          </button>
        ))}
      </div>

      {/* Swipeable track */}
      <div className="mt-3 flex w-full min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={() => goTo(index - 1, "arrow")}
          disabled={index === 0}
          aria-label={intl.formatMessage({ id: "a11y.card.previous" })}
          className="hidden h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-sec transition-colors duration-200 hover:bg-blue-ulight hover:text-brand disabled:pointer-events-none disabled:opacity-0 lg:flex"
        >
          <FaChevronLeft />
        </button>

        {/*
          min-w-0 is what stops this track from sizing to its max-content — the
          three slides laid out side by side — and dragging the whole card
          past the edge of a phone screen.
        */}
        <div className="w-full min-w-0 flex-1 overflow-hidden">
          <motion.div
            className="flex touch-pan-y"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={onDragEnd}
            animate={{ x: `-${index * 100}%` }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
          >
            {SLIDES.map((slide, slideIndex) => {
              const isCurrent = slideIndex === index;
              return (
                <div
                  key={slide.id}
                  className="w-full min-w-0 shrink-0 grow-0 basis-full px-1"
                  aria-hidden={!isCurrent}
                >
                  <div className="flex flex-col items-center">
                    <Link
                      href={links[slide.id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      tabIndex={isCurrent ? undefined : -1}
                      onClick={() => {
                        trackEvent(
                          "card-qr-link",
                          { platform: slide.id },
                          { criticalOnNav: true },
                        );
                        trackCtaClick("card", slide.id);
                      }}
                      className="group rounded-3xl border border-blue-light bg-surface p-2.5 shadow-sm transition-all duration-200 hover:border-blue-sec hover:shadow-md lg:p-3"
                    >
                      {/*
                        The QR is drawn from a fixed module grid, so it only
                        stays scannable while it keeps its 1:1 ratio and its
                        white quiet zone — never crop or tint it.
                      */}
                      <slide.Qr
                        className="h-60 w-60 max-w-full select-none lg:h-56 lg:w-56"
                        role="img"
                        aria-label={intl.formatMessage(
                          { id: "a11y.card.qr" },
                          { platform: intl.formatMessage({ id: slide.labelKey }) },
                        )}
                      />
                    </Link>

                    <p className="mt-2.5 text-sm font-bold text-title-blue">
                      <FormattedMessage id={slide.storeKey} />
                    </p>
                    <p className="text-xs text-gray-main">
                      <FormattedMessage id={slide.deviceKey} />
                    </p>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        <button
          type="button"
          onClick={() => goTo(index + 1, "arrow")}
          disabled={index === SLIDES.length - 1}
          aria-label={intl.formatMessage({ id: "a11y.card.next" })}
          className="hidden h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-sec transition-colors duration-200 hover:bg-blue-ulight hover:text-brand disabled:pointer-events-none disabled:opacity-0 lg:flex"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Progress dots + swipe affordance */}
      <div className="mt-2.5 flex items-center gap-2">
        {SLIDES.map((slide, slideIndex) => (
          <span
            key={slide.id}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              slideIndex === index ? "w-6 bg-blue-main" : "w-1.5 bg-blue-sec"
            }`}
          />
        ))}
      </div>

    </section>
  );
}
